/**
 * useInventoryData - state and API logic for the Inventory component
 */
import { useState, useEffect, useRef } from 'react';
import { warehouseService } from '../../../services/warehouseService';
import { productService } from '../../../services/productService';

export function useInventoryData(locationId) {
  const [products, setProducts] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isInventoryStarted, setIsInventoryStarted] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // Partial inventory
  const [inventoryType, setInventoryType] = useState('full');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);

  // History
  const [inventorySessions, setInventorySessions] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Session editing
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionItems, setSessionItems] = useState([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const [addProductSearch, setAddProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSessionReadOnly, setIsSessionReadOnly] = useState(false);
  // Per-item save errors for session editing (itemId -> message)
  const [saveErrors, setSaveErrors] = useState({});

  // Debounce + sequence guard for per-item PUT saves.
  // saveStateRef.current[itemId] = { timer, value, seq }
  const saveStateRef = useRef({});
  // Ref mirror of saveErrors so completeSession can read the freshest state
  // synchronously right after flushing pending saves.
  const saveErrorsRef = useRef({});
  const SAVE_DEBOUNCE_MS = 400;

  const setSaveError = (itemId, msg) => {
    if (msg) saveErrorsRef.current[itemId] = msg;
    else delete saveErrorsRef.current[itemId];
    setSaveErrors({ ...saveErrorsRef.current });
  };

  const resetSaveState = () => {
    Object.values(saveStateRef.current).forEach(entry => {
      if (entry?.timer) clearTimeout(entry.timer);
    });
    saveStateRef.current = {};
    saveErrorsRef.current = {};
    setSaveErrors({});
  };

  // Load categories + active inventory on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const categoriesResult = await productService.getCategories();
        if (categoriesResult.success) setCategories(categoriesResult.data || []);

        const activeInventory = await warehouseService.getActiveInventory();
        if (activeInventory.success && activeInventory.data) {
          setIsInventoryStarted(true);
          setInventoryData(activeInventory.data.products || []);
        }
      } catch (err) {
        console.error('Blad ladowania danych:', err);
      }
    };
    loadInitialData();
  }, []);

  // Load sessions when history tab is active
  useEffect(() => {
    if (activeTab === 'history') loadInventorySessions();
  }, [activeTab, dateFilter, statusFilter]);

  // Debounced product preview search (full inventory mode)
  useEffect(() => {
    if (!searchTerm && !selectedCategory) return;
    const t = setTimeout(async () => {
      try {
        const result = await productService.searchProducts({ search: searchTerm, category: selectedCategory, locationId });
        setProducts(result?.products || []);
      } catch (err) {
        console.error('Blad ladowania produktow:', err);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm, selectedCategory, locationId]);

  // Debounced search for session product add
  useEffect(() => {
    if (!addProductSearch || addProductSearch.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const result = await productService.searchProducts({ search: addProductSearch, locationId });
        setSearchResults(result?.products || []);
      } catch (err) {
        console.error('Blad wyszukiwania produktow:', err);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [addProductSearch, locationId]);

  // Debounced search for partial inventory selection
  useEffect(() => {
    if (!productSearchTerm || productSearchTerm.length < 2) { setProductSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const result = await productService.searchProducts({ search: productSearchTerm, category: selectedCategory, locationId });
        setProductSearchResults(result?.products || []);
      } catch (err) {
        console.error('Błąd wyszukiwania produktów:', err);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [productSearchTerm, selectedCategory, locationId]);

  // ---- Actions ----

  const loadInventorySessions = async () => {
    try {
      const result = await warehouseService.getInventorySessions({ date: dateFilter, status: statusFilter, location_id: locationId });
      if (result.success) setInventorySessions(result.data || []);
    } catch (err) {
      console.error('Blad ladowania sesji:', err);
    }
  };

  const addToSelectedProducts = (product) => {
    if (!selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts(prev => [...prev, { ...product, system_count: product.stock_quantity || 0 }]);
    }
    setProductSearchTerm('');
    setProductSearchResults([]);
  };

  const removeFromSelectedProducts = (productId) => setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  const clearSelectedProducts = () => setSelectedProducts([]);

  const startInventory = async () => {
    if (inventoryType === 'partial' && selectedProducts.length === 0) {
      setError('Wybierz co najmniej jeden produkt do inwentaryzacji czesciowej');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = { location_id: locationId, inventory_type: inventoryType };
      if (inventoryType === 'full') {
        params.category = selectedCategory;
      } else {
        params.product_ids = selectedProducts.map(p => p.id);
      }
      const result = await warehouseService.startInventory(params);
      if (result.success) {
        setIsInventoryStarted(true);
        setInventoryData(result.data.products || []);
        setSuccess(`Inwentaryzacja ${inventoryType === 'full' ? 'calosciowa' : 'czesciowa'} zostala rozpoczeta`);
        setSelectedProducts([]);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Blad podczas rozpoczynania inwentaryzacji');
      }
    } catch (_) {
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const addProductToActiveInventory = (product) => {
    const productId = product.id;
    // Sprawdź czy produkt już jest w inwentaryzacji
    if (inventoryData.some(item => item.product_id === productId)) return;
    setInventoryData(prev => [...prev, {
      product_id: productId,
      product_name: product.name || product.nazwa || '',
      product_description: product.description || product.opis || '',
      product_code: product.barcode || product.kod_produktu || product.ean || '',
      system_count: product.stock_quantity || 0,
      actual_count: null,
      unit: product.unit || product.jednostka || 'szt'
    }]);
  };

  const updateInventoryCount = (productId, actualCount) => {
    setInventoryData(prev => prev.map(item =>
      item.product_id === productId ? { ...item, actual_count: parseInt(actualCount) || 0 } : item
    ));
  };

  const finishInventory = async () => {
    const missingCounts = inventoryData.filter(item => item.actual_count === undefined || item.actual_count === null);
    if (missingCounts.length > 0) {
      setError('Wprowadz stany dla wszystkich produktow przed zakonczeniem inwentaryzacji');
      return;
    }
    setLoading(true);
    try {
      const result = await warehouseService.finishInventory({
        products: inventoryData.map(item => ({
          product_id: item.product_id,
          system_count: item.system_count,
          actual_count: item.actual_count,
          difference: item.actual_count - item.system_count
        }))
      });
      if (result.success) {
        let msg = 'Inwentaryzacja zostala zakonczona pomyslnie.';
        if (result.data?.pw_document) msg += ` PW: ${result.data.pw_document} (${result.data.pw_items_count} poz.)`;
        if (result.data?.rw_document) msg += ` RW: ${result.data.rw_document} (${result.data.rw_items_count} poz.)`;
        setSuccess(msg);
        setIsInventoryStarted(false);
        setInventoryData([]);
        loadInventorySessions();
        setTimeout(() => setSuccess(''), 8000);
      } else {
        setError(result.error || 'Blad podczas kończenia inwentaryzacji');
      }
    } catch (_) {
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const cancelInventory = async () => {
    if (!window.confirm('Czy na pewno chcesz anulowac biezaca inwentaryzacje? Wprowadzone stany zostana utracone.')) return;
    setLoading(true);
    setError('');
    try {
      const result = await warehouseService.cancelInventory();
      if (result.success) {
        setIsInventoryStarted(false);
        setInventoryData([]);
        setSuccess('Inwentaryzacja zostala anulowana');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Nie czyscimy stanu jesli serwer nie potwierdzil anulowania
        setError(result.error || 'Blad podczas anulowania inwentaryzacji');
      }
    } catch (_) {
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  // Session editing
  const openSession = async (session) => {
    if (session.status !== 'active') return;
    setLoadingSession(true);
    setError('');
    setIsSessionReadOnly(false);
    resetSaveState();
    try {
      const result = await warehouseService.getInventorySession(session.id);
      if (result.success) {
        setSelectedSession(result.data);
        setSessionItems(result.data.items || []);
      } else {
        setError(result.error || 'Blad pobierania sesji');
      }
    } catch (_) {
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoadingSession(false);
    }
  };

  const viewSession = async (session) => {
    setLoadingSession(true);
    setError('');
    setIsSessionReadOnly(true);
    resetSaveState();
    try {
      const result = await warehouseService.getInventorySession(session.id);
      if (result.success) {
        setSelectedSession(result.data);
        setSessionItems(result.data.items || []);
      } else {
        setError(result.error || 'Blad pobierania sesji');
      }
    } catch (_) {
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoadingSession(false);
    }
  };

  const closeSession = () => {
    resetSaveState();
    setSelectedSession(null);
    setSessionItems([]);
    setAddProductSearch('');
    setSearchResults([]);
    setIsSessionReadOnly(false);
  };

  // Wysyla pojedynczy zapis pozycji do serwera z gwarancja kolejnosci.
  // Zwraca true przy sukcesie, false przy bledzie (blad jest tez zapisany
  // w saveErrorsRef/saveErrors zeby zablokowac zakonczenie sesji).
  const performItemSave = async (sessionId, itemId) => {
    const entry = saveStateRef.current[itemId];
    if (!entry) return true;
    entry.timer = null;
    const value = entry.value;
    // Sekwencja: kazde wyslanie dostaje nowy numer; odpowiedzi starszych
    // (out-of-order) requestow sa ignorowane, ostatni zapis wygrywa.
    const seq = (entry.seq || 0) + 1;
    entry.seq = seq;

    let result;
    try {
      result = await warehouseService.updateInventoryItem(sessionId, itemId, value);
    } catch (err) {
      result = { success: false, error: err?.message || 'Blad polaczenia z serwerem' };
    }

    const current = saveStateRef.current[itemId];
    // Nowszy zapis tej samej pozycji jest juz w toku – ignorujemy stara odpowiedz.
    if (!current || current.seq !== seq) return result.success;

    if (result.success) {
      setSaveError(itemId, null);
      return true;
    }
    const msg = result.error || 'Blad zapisu pozycji';
    setSaveError(itemId, msg);
    setError(`Nie udalo sie zapisac stanu pozycji: ${msg}`);
    return false;
  };

  const updateSessionItemCount = (itemId, actualCount) => {
    const value = parseInt(actualCount) || 0;
    // Optymistyczna aktualizacja UI
    setSessionItems(prev => prev.map(item =>
      item.id === itemId
        ? { ...item, actual_count: value, difference: value - item.system_count }
        : item
    ));

    const sessionId = selectedSession?.id;
    if (!sessionId) return;

    // Debounce: kazdy znak nie wysyla osobnego PUT; wysylamy dopiero po
    // przerwie w pisaniu. Poprzedni timer tej pozycji jest kasowany.
    const prevEntry = saveStateRef.current[itemId] || {};
    if (prevEntry.timer) clearTimeout(prevEntry.timer);
    const entry = { ...prevEntry, value };
    entry.timer = setTimeout(() => {
      performItemSave(sessionId, itemId);
    }, SAVE_DEBOUNCE_MS);
    saveStateRef.current[itemId] = entry;
  };

  // Natychmiast wysyla wszystkie oczekujace (zdebounce'owane) zapisy i czeka
  // na ich zakonczenie. Zwraca true jesli wszystkie sie powiodly.
  const flushPendingItemSaves = async () => {
    const sessionId = selectedSession?.id;
    if (!sessionId) return true;
    const pendingIds = Object.keys(saveStateRef.current).filter(
      id => saveStateRef.current[id]?.timer
    );
    pendingIds.forEach(id => {
      const entry = saveStateRef.current[id];
      if (entry?.timer) { clearTimeout(entry.timer); entry.timer = null; }
    });
    const results = await Promise.all(
      pendingIds.map(id => performItemSave(sessionId, id))
    );
    return results.every(Boolean);
  };

  const deleteSessionItem = async (itemId) => {
    if (!window.confirm('Czy na pewno chcesz usunac te pozycje?')) return;
    try {
      const result = await warehouseService.deleteInventoryItem(selectedSession.id, itemId);
      if (result.success) {
        setSessionItems(prev => prev.filter(item => item.id !== itemId));
        setSuccess('Pozycja usunieta');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(result.error || 'Blad usuwania pozycji');
      }
    } catch (_) {
      setError('Blad polaczenia z serwerem');
    }
  };

  const addProductToSession = async (product) => {
    try {
      const result = await warehouseService.addInventoryItem(selectedSession.id, product.id, 0);
      if (result.success) {
        const sessionResult = await warehouseService.getInventorySession(selectedSession.id);
        if (sessionResult.success) setSessionItems(sessionResult.data.items || []);
        setAddProductSearch('');
        setSearchResults([]);
        setSuccess('Produkt dodany');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(result.error || 'Blad dodawania produktu');
      }
    } catch (_) {
      setError('Blad polaczenia z serwerem');
    }
  };

  const completeSession = async () => {
    const missingCounts = sessionItems.filter(item => item.actual_count === undefined || item.actual_count === null);
    if (missingCounts.length > 0) {
      setError('Wprowadz stany dla wszystkich produktow przed zakonczeniem inwentaryzacji');
      return;
    }
    if (!window.confirm('Czy na pewno chcesz zakonczyc te inwentaryzacje? Zostana wygenerowane dokumenty PW/RW.')) return;

    setLoading(true);
    // Dokumenty PW/RW sa budowane z bazy, nie z frontu. Najpierw domykamy
    // wszystkie zdebounce'owane zapisy i sprawdzamy, czy zaden nie padl –
    // inaczej powstalyby ciche rozbieznosci miedzy widokiem a dokumentami.
    const flushedOk = await flushPendingItemSaves();
    if (!flushedOk || Object.keys(saveErrorsRef.current).length > 0) {
      setLoading(false);
      setError('Niektore stany pozycji nie zostaly zapisane. Popraw i zapisz je przed zakonczeniem inwentaryzacji.');
      return;
    }
    try {
      const result = await warehouseService.completeInventorySession(selectedSession.id);
      if (result.success) {
        let msg = 'Inwentaryzacja zostala zakonczona pomyslnie.';
        if (result.data?.pw_document) msg += ` PW: ${result.data.pw_document} (${result.data.pw_items_count} poz.)`;
        if (result.data?.rw_document) msg += ` RW: ${result.data.rw_document} (${result.data.rw_items_count} poz.)`;
        setSuccess(msg);
        closeSession();
        loadInventorySessions();
        setTimeout(() => setSuccess(''), 8000);
      } else {
        setError(result.error || 'Blad podczas kończenia inwentaryzacji');
      }
    } catch (_) {
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    products, inventoryData, searchTerm, setSearchTerm,
    loading, error, setError, success, setSuccess,
    isInventoryStarted,
    categories, selectedCategory, setSelectedCategory,
    inventoryType, setInventoryType,
    selectedProducts,
    productSearchTerm, setProductSearchTerm,
    productSearchResults,
    inventorySessions,
    activeTab, setActiveTab,
    dateFilter, setDateFilter,
    statusFilter, setStatusFilter,
    selectedSession, sessionItems,
    loadingSession,
    addProductSearch, setAddProductSearch,
    searchResults,
    isSessionReadOnly,
    saveErrors,
    // Actions
    startInventory, updateInventoryCount, addProductToActiveInventory,
    finishInventory, cancelInventory,
    addToSelectedProducts, removeFromSelectedProducts, clearSelectedProducts,
    openSession, viewSession, closeSession,
    updateSessionItemCount, deleteSessionItem, addProductToSession, completeSession,
    loadInventorySessions
  };
}
